import React, { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../LanguageContext";
import dasImg from "../assets/products/das/DAS.png";
import das48Img from "../assets/products/das/DAS48.png";
import dmsImg from "../assets/products/dms/dms.png";
import bmsImg from "../assets/products/bms/bmsnew.png";
import monImg from "../assets/products/monkwh/monkwh putih.jpg";
import minidcuImg from "../assets/products/dcu/miniDCUnew.png";
import watermeterImg from "../assets/products/watermeter/daswater.png";
import hmiImg from "../assets/products/hmi/hmimonkwh.png";
import hmidasImg from "../assets/products/hmi/hmi.png";
import hmiscadaImg from "../assets/products/hmi/hmiscada.png";

const products = [
  { id: 1, name: "DAS24p", fullName: "Digital Alert System 24P", category: "Hardware", img: dasImg },
  { id: 2, name: "DAS48", fullName: "Digital Alert System 48P", category: "Hardware", img: das48Img },
  { id: 3, name: "DMS", fullName: "Device Monitoring System", category: "Hardware", img: dmsImg },
  { id: 4, name: "BMS", fullName: "Battery Monitoring System", category: "Hardware", img: bmsImg },
  { id: 5, name: "MonKWH", fullName: "Energy Monitoring System", category: "Hardware", img: monImg },
  { id: 6, name: "Mini DCU", fullName: "Data Concentrator Unit", category: "Hardware", img: minidcuImg },
  { id: 7, name: "Water Meter", fullName: "DAS Water Meter", category: "Hardware", img: watermeterImg },
  { id: 8, name: "HMI IOT", fullName: "HMI IoT Monitoring Dashboard", category: "Software", img: hmiImg },
  { id: 9, name: "HMI OMC", fullName: "HMI Operation Maintenance Center - OMC", category: "Software", img: hmidasImg },
  { id: 10, name: "HMI SCADA", fullName: "HMI SCADA Air Monitoring System", category: "Software", img: hmiscadaImg },
];

const fadeUp = {
  hidden: { opacity: 0, y: 50, scale: 0.95 },
  visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.8, ease: "easeOut" } },
};

export default function ProductSection() {
  const [filter, setFilter] = useState("All");
  const [activeIndex, setActiveIndex] = useState(0);
  const navigate = useNavigate();
  const { language } = useLanguage();

  const filteredProducts = filter === "All" ? products : products.filter((p) => p.category === filter);
  const featured = filteredProducts[activeIndex];

  const goToDetail = (productName) => {
    navigate(`/product/${productName.toLowerCase().replace(/\s+/g, "")}`);
  };

  const texts = {
    en: { title: "Browse Our Product", all: "All", hardware: "Hardware", software: "Software" },
    id: { title: "Jelajahi Produk Kami", all: "Semua", hardware: "Perangkat Keras", software: "Perangkat Lunak" },
  };

  const t = texts[language] || texts.en;

  return (
    <section className="py-24 bg-white px-4 sm:px-10" id="products">
      <motion.h2
        className={`text-center font-extrabold mb-12 ${language === "id" ? "text-4xl" : "text-5xl"}`}
        style={{ color: "#1446A5" }}
        variants={fadeUp}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: false, amount: 0.3 }}
      >
        {t.title}
      </motion.h2>

      <motion.div className="flex justify-center gap-3 flex-wrap mb-12">
        {[t.all, t.hardware, t.software].map((cat, idx) => {
          const catValue = ["All", "Hardware", "Software"][idx];
          return (
            <motion.button
              key={cat}
              onClick={() => { setFilter(catValue); setActiveIndex(0); }}
              className={`px-4 sm:px-6 py-2 rounded-lg border-2 font-semibold transition text-sm sm:text-base ${
                filter === catValue ? "bg-[#1446A5] text-white border-[#1446A5]" : "border-gray-300 text-gray-600 hover:border-[#1446A5]"
              }`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {cat}
            </motion.button>
          );
        })}
      </motion.div>

      <div className="flex flex-col lg:flex-row gap-12 lg:gap-16 items-start justify-center">
        <motion.div
          className="flex-shrink-0 flex flex-col items-center lg:items-start"
          key={featured.id}
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false }}
        >
          <motion.img
            src={featured.img}
            alt={featured.name}
            className="w-[250px] sm:w-[320px] lg:w-[400px] h-auto object-contain mb-6"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          />
          <div className="bg-[#1446A5] text-white px-4 sm:px-6 py-2 rounded-lg flex items-center justify-between w-[220px] sm:w-[280px] lg:w-[350px]">
            <span className="font-semibold text-sm sm:text-base">{featured.fullName}</span>
            <button
              onClick={() => goToDetail(featured.name)}
              className="bg-white text-[#1446A5] rounded-full w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center hover:scale-110 transition text-sm sm:text-base"
            >
              ➜
            </button>
          </div>
        </motion.div>

        <motion.div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 gap-4 sm:gap-6 w-full" variants={fadeUp} initial="hidden" whileInView="visible">
          {filteredProducts.filter((_, index) => index !== activeIndex).slice(0, 9).map((item, i) => (
            <motion.div
              key={item.id}
              className="flex flex-col items-center cursor-pointer hover:opacity-90 transition"
              onClick={() => setActiveIndex(filteredProducts.findIndex((p) => p.id === item.id))}
              whileHover={{ scale: 1.08 }}
              whileTap={{ scale: 0.95 }}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1, duration: 0.6 }}
            >
              <img src={item.img} alt={item.name} className="w-40 sm:w-40 h-40 sm:h-40 object-contain" />
              <p className="text-gray-700 mt-2 font-medium text-center text-xs sm:text-sm">{item.fullName}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
